import { ApiOutlined, GlobalOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { Base } from 'zf-web-components';
import { useContent } from '@hooks';
import { OAuthConsentStore } from './store';
import styles from './style.module.less';
import type { ReactElement } from 'react';
import type { OAuthDecision } from './types';

function OAuthConsentPage(): ReactElement {
  const { store, t } = useContent(OAuthConsentStore)();

  const handleDecision = async (decision: OAuthDecision): Promise<void> => {
    const redirectUri = await store.onDecision(decision);
    if (redirectUri != null) {
      window.location.assign(redirectUri);
    }
  };

  const handleAllow = async (): Promise<void> => await handleDecision('allow');
  const handleDeny = async (): Promise<void> => await handleDecision('deny');

  let content: ReactElement;
  if (store.loading) {
    content = (
      <Base.Card className={styles.consentCard}>
        <Base.Skeleton active paragraph={{ rows: 6 }} />
      </Base.Card>
    );
  } else if (store.unavailable || store.interaction == null) {
    content = (
      <Base.Card className={styles.consentCard}>
        <Base.Result
          status='warning'
          title={t('授权请求不可用')}
          subTitle={t('该授权请求已失效，请返回 MCP 客户端重新连接。')}
        />
      </Base.Card>
    );
  } else {
    const { interaction } = store;
    const resourceName = t(interaction.resourceName);

    content = (
      <Base.Card className={styles.consentCard}>
        <div className={styles.clientSummary}>
          <div className={styles.clientCopy}>
            <h1 className={styles.title}>
              {t('允许 {clientName} 连接 {resourceName}？', {
                clientName: interaction.clientName,
                resourceName
              })}
            </h1>
            <p className={styles.description}>
              {t('该客户端将使用以下平台账号访问 {resourceName}。', {
                resourceName
              })}
            </p>
            {interaction.clientHostname != null && (
              <div className={styles.clientHostname}>
                <GlobalOutlined />
                <span>{interaction.clientHostname}</span>
              </div>
            )}
          </div>
        </div>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('授权账号')}</h2>
          <div className={styles.accountCard}>
            <Base.Avatar className={styles.userAvatar} icon={<UserOutlined />} size={40} />
            <div className={styles.accountIdentity}>
              <strong>{interaction.user.name}</strong>
              <span>{interaction.user.id}</span>
            </div>
            <Base.Tag className={styles.tenantTag}>{t('当前租户')}: {interaction.user.tenant}</Base.Tag>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('客户端将获得')}</h2>
          <div className={styles.permissionPanel}>
            <div className={styles.permissionItem}>
              <span className={styles.permissionIcon}><ApiOutlined /></span>
              <div className={styles.permissionCopy}>
                <strong>{t('以你的身份调用 {resourceName}', { resourceName })}</strong>
                <span>{t('访问范围与当前账号的角色权限和业务数据权限一致。')}</span>
              </div>
            </div>
            <div className={styles.passwordNotice}>
              <LockOutlined />
              <span>{t('客户端不会获得你的平台登录密码。')}</span>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('连接信息')}</h2>
          <dl className={styles.detailList}>
            <div className={styles.detailRow}>
              <dt>{t('回调主机')}</dt>
              <dd>{interaction.redirectHostname}</dd>
            </div>
            <div className={styles.detailRow}>
              <dt>{t('MCP 地址')}</dt>
              <dd>{interaction.resource}</dd>
            </div>
          </dl>
        </section>

        {store.isLoopbackRedirect && (
          <Base.Alert
            className={styles.loopbackAlert}
            message={t('回调地址位于本机，请确认这是你正在使用的 MCP 客户端。')}
            type='warning'
            showIcon
          />
        )}

        <div className={styles.actions}>
          <p className={styles.actionHint}>{t('仅在你信任此客户端时继续。')}</p>
          <div className={styles.actionButtons}>
            <Base.Button disabled={store.isSubmitting} loading={store.isDenying} onClick={handleDeny}>
              {t('取消')}
            </Base.Button>
            <Base.Button disabled={store.isSubmitting} loading={store.isAllowing} type='primary' onClick={handleAllow}>
              {t('允许并继续')}
            </Base.Button>
          </div>
        </div>
      </Base.Card>
    );
  }

  return (
    <Base.Layout className={styles.pageContainer}>
      <div className={styles.pageShell}>
        <header className={styles.brand}>
          {store.interaction != null && (
            <img
              alt=''
              className={styles.brandLogo}
              src={store.brandLogo}
            />
          )}
          <span>{store.interaction == null ? t('MCP 授权') : t(store.interaction.resourceName)}</span>
        </header>
        {content}
      </div>
    </Base.Layout>
  );
}

export default observer(OAuthConsentPage);
